'use client'

import { X, Calendar, User, Hash, Phone, Globe, Tag, AlertCircle } from 'lucide-react'
import { Campaign } from '../lib/mock-data'
import { SeverityPill, StatusPill } from './pills'

interface Finding {
  title: string
  description: string
  severity: 'high' | 'medium'
}

function getFindings(c: Campaign): Finding[] {
  const findings: Finding[] = []

  if (c.sourceType === 'domain') {
    if (c.domainActive) {
      findings.push({
        title: 'Domínio activo e operacional',
        description: `O domínio "${c.domain}" continua activo e pode estar a recolher dados de vítimas neste momento.`,
        severity: 'high',
      })
    } else if (c.domain) {
      findings.push({
        title: 'Domínio fraudulento identificado',
        description: `O domínio "${c.domain}" foi identificado como fraudulento. O takedown já foi accionado.`,
        severity: 'medium',
      })
    }
    if (c.type === 'Phishing' && c.domain) {
      findings.push({
        title: 'Engenharia social — recolha de credenciais',
        description: 'A mensagem recorre a urgência e imitação de fontes legítimas para obter credenciais ou dados sensíveis.',
        severity: 'high',
      })
    }
    if (c.type === 'Marca') {
      findings.push({
        title: 'Uso indevido de marca registada',
        description: 'O conteúdo utiliza activos visuais e linguagem das marcas do Grupo Impresa para enganar a vítima.',
        severity: 'medium',
      })
    }
  }

  if (c.sourceType === 'entity') {
    if (c.entityReference?.includes('IBAN') || c.entityReferenceValue?.startsWith('PT')) {
      findings.push({
        title: 'Transferência bancária fraudulenta',
        description: 'O IBAN apresentado não corresponde a qualquer conta legítima do Grupo Impresa. Nunca transfira sem confirmação por canal oficial.',
        severity: 'high',
      })
    }
    if (c.entityReference?.includes('Entidade')) {
      findings.push({
        title: 'Referência Multibanco não autorizada',
        description: 'A entidade e referência foram verificadas e não correspondem a qualquer cobrança legítima da empresa.',
        severity: 'high',
      })
    }
    if (c.type === 'Phishing') {
      findings.push({
        title: 'CEO Fraud / BEC',
        description: 'A mensagem imita comunicação urgente de um executivo para pressionar uma transferência não autorizada — técnica conhecida como Business Email Compromise.',
        severity: 'high',
      })
    }
  }

  if (c.sourceType === 'scall') {
    findings.push({
      title: 'Chamada de voz fraudulenta (Vishing)',
      description: 'O número reportado está associado a campanhas de vishing. Nunca forneça dados pessoais ou credenciais por telefone sem verificação.',
      severity: 'high',
    })
    if (c.callType?.includes('Suporte')) {
      findings.push({
        title: 'Falso suporte técnico',
        description: 'Equipas de IT internas nunca solicitam acesso remoto ou credenciais por telefone de forma não solicitada.',
        severity: 'high',
      })
    }
    if (c.callType?.includes('Prémio')) {
      findings.push({
        title: 'Fraude de prémio falso',
        description: 'O Grupo Impresa não notifica prémios por telefone. Qualquer pedido de dados neste contexto é fraude.',
        severity: 'medium',
      })
    }
  }

  if (findings.length === 0) {
    findings.push({
      title: 'Mensagem suspeita reportada',
      description: 'Esta ocorrência foi reportada como suspeita e está a ser analisada pela equipa de Confiança e Segurança.',
      severity: 'medium',
    })
  }

  return findings.sort((a, b) => (a.severity === 'high' && b.severity !== 'high' ? -1 : 1))
}

export function CampaignModal({
  campaign,
  onClose,
}: {
  campaign: Campaign | null
  onClose: () => void
}) {
  if (!campaign) return null

  const findings = getFindings(campaign)
  const description = campaign.description ?? campaign.messageContent

  return (
    <div
      className="fixed inset-0 z-50 flex items-end justify-center bg-black/50 sm:items-center p-0 sm:p-4"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose()
      }}
    >
      <div className="relative flex max-h-[92vh] w-full max-w-2xl flex-col overflow-hidden rounded-t-2xl bg-card shadow-elegant sm:rounded-2xl">
        <button
          onClick={onClose}
          className="absolute right-4 top-4 z-10 flex h-8 w-8 items-center justify-center rounded-full bg-secondary text-muted-foreground transition-colors hover:bg-border"
        >
          <X className="h-4 w-4" />
        </button>

        <div className="overflow-y-auto px-6 pb-10 pt-6">
          <div className="mb-3 flex flex-wrap items-center gap-2 pr-10">
            <span className="flex items-center gap-1.5 text-[11px] text-muted-foreground">
              <Calendar className="h-3 w-3" />
              {new Date(campaign.createdAt).toLocaleDateString('pt-PT', {
                day: 'numeric',
                month: 'short',
                year: 'numeric',
              })}
            </span>

            {campaign.sourceType === 'domain' && campaign.domain && (
              <span
                className={`rounded-full border px-2.5 py-0.5 text-[11px] font-semibold ${
                  campaign.domainActive
                    ? 'border-red-200 bg-red-50 text-red-800'
                    : 'border-border bg-secondary text-muted-foreground'
                }`}
              >
                {campaign.domainActive ? '● Domínio activo' : '○ Domínio inactivo'}
              </span>
            )}

            {campaign.sourceType === 'scall' && campaign.callType && (
              <span className="rounded-full border border-amber-200 bg-amber-50 px-2.5 py-0.5 text-[11px] font-semibold text-amber-800">
                {campaign.callType}
              </span>
            )}

            <div className="ml-auto flex items-center gap-1.5">
              <SeverityPill severity={campaign.severity} />
              <StatusPill status={campaign.status} />
            </div>
          </div>

          <h2 className="font-display text-xl font-semibold leading-snug text-foreground">
            {campaign.title}
          </h2>
          <p className="mt-1 mb-5 text-[11px] text-muted-foreground">
            {campaign.type} · {campaign.channel} · Reportado por {campaign.reporter} · {campaign.date}
          </p>

          <div className="space-y-2 mb-5">
            {campaign.sender && (
              <div className="flex items-center gap-2 rounded-lg bg-secondary px-3 py-2.5">
                {campaign.sourceType === 'scall' ? (
                  <Phone className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
                ) : (
                  <User className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
                )}
                <span className="text-[11px] text-muted-foreground">Remetente:</span>
                <span className="text-[11px] font-semibold text-foreground">{campaign.sender}</span>
              </div>
            )}

            {campaign.sourceType === 'entity' && campaign.entityReference && (
              <div className="flex flex-wrap items-center gap-2 rounded-lg border border-blue-100 bg-blue-50 px-3 py-2.5">
                <Hash className="h-3.5 w-3.5 shrink-0 text-blue-600" />
                <span className="text-[11px] font-medium text-blue-700">
                  {campaign.entityReference}:
                </span>
                <span className="font-mono text-[11px] font-semibold text-blue-900">
                  {campaign.entityReferenceValue}
                </span>
              </div>
            )}

            {campaign.sourceType === 'domain' && campaign.domain && (
              <div className="flex items-center gap-2 rounded-lg bg-secondary px-3 py-2.5">
                <Globe className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
                <span className="text-[11px] text-muted-foreground">Domínio:</span>
                <span className="font-mono text-[11px] font-semibold text-foreground">
                  {campaign.domain}
                </span>
              </div>
            )}
          </div>

          <div className="mb-6 rounded-lg bg-secondary/60 p-4">
            <p className="text-sm leading-relaxed text-foreground">{description}</p>
          </div>

          <div className="mb-5 h-px bg-border" />

          <h3 className="font-display mb-3 text-sm font-semibold text-foreground">
            Por que é suspeito?
          </h3>
          <div className="mb-5 space-y-2.5">
            {findings.map((f, i) => (
              <div
                key={i}
                className={`rounded-lg border-l-[3px] p-3 ${
                  f.severity === 'high'
                    ? 'border-red-500 bg-red-50'
                    : 'border-amber-400 bg-amber-50'
                }`}
              >
                <div className="mb-1 flex items-center gap-2">
                  <AlertCircle
                    className={`h-4 w-4 shrink-0 ${
                      f.severity === 'high' ? 'text-red-600' : 'text-amber-600'
                    }`}
                  />
                  <span
                    className={`text-xs font-semibold ${
                      f.severity === 'high' ? 'text-red-800' : 'text-amber-800'
                    }`}
                  >
                    {f.title}
                  </span>
                </div>
                <p
                  className={`text-xs leading-relaxed ${
                    f.severity === 'high' ? 'text-red-700' : 'text-amber-700'
                  }`}
                >
                  {f.description}
                </p>
              </div>
            ))}
          </div>

          {campaign.tags.length > 0 && (
            <div>
              <h3 className="font-display mb-2.5 text-sm font-semibold text-foreground">
                Categorias
              </h3>
              <div className="flex flex-wrap gap-2">
                {campaign.tags.map((tag) => (
                  <span
                    key={tag}
                    className="flex items-center gap-1.5 rounded-full bg-secondary px-3 py-1.5 text-[11px] font-medium text-muted-foreground"
                  >
                    <Tag className="h-2.5 w-2.5" />
                    {tag}
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
